import React, { useEffect, useRef, useState } from "react";
import "../Contacts/Contacts.css";
import {
  CONTACT_CALL_ICON,
  FAVORITE_STAR_SELECTED,
  FAVORITE_STAR_UNSELECTED,
  SHIP_BADGE_ICON,
  SHORE_BADGE_ICON,
  SINGLE_CHAT_ICON,
} from "../../../utils/constants";
import CallButton from "../../Telephony/CallButton";
import { Stack, Box, styled, Badge, Grid, Typography, Avatar } from "@mui/material";
import { getColorForRankAndRoles } from '../../../utils/ColorMapping';
import CachedImage from "../../../components/common/CachedImage";
import { ImagePath } from "../../../assets/ImagePath";
import Chip from "../../../components/ui/Chip";
import { getUserProfileImageUrl, getUserProfileImageUrlThumbnail, isValidImageUrl } from "../../../utils/general";

const StyledBadge = styled(Badge)(({ theme }) => ({
  "& .MuiBadge-badge": {
    right: "4px",
    bottom: "10px",
    transform: "scale(1) translate(50%, 50%)",
  },
}));


const MentorBadge = styled(Box)({
  width: "1.5rem",
  borderRadius: "50%",
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontWeight: '700',
  fontFamily: 'Figtree-Regular',
  fontSize: '13px',
  height: "1.5rem",
  backgroundColor: '#376AFA',
  color: "#fff"
});

const categoryColors = {
  Shore: "#FF6347",
  Ship: "#05BC52",
  Classification: "#FFC800",
  Deck: "#376AFA",
  Personal: "#727484",
  General: "#FFC800",
};

const getAvatarStyles = (imgSrc) => ({
  bgcolor: imgSrc ? "transparent" : "#638dff",
  width: 40,
  height: 40,
  fontSize: "14px",
});

const getContactNameStyles = () => ({
  color: "#404585",
  fontSize: "18px",
  fontWeight: 500,
  minWidth: "206px",
});

const getContactDetailsStyles = () => ({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  flexWrap: "nowrap",
  marginTop: "0.25rem"
});

const alignGridItems = () => ({
  display: "flex",
  alignItems: "center",
  justifyContent: 'center',
});

const getContactRankStyles = () => ({
  fontWeight: "400",
  fontSize: "13px",
  color: "#404585",
});

const getFavouriteIconStyles = (contact) => ({
  pointerEvents: contact?.favActionInProgress ? 'none' : 'auto',
  cursor: contact?.favActionInProgress ? 'default' : 'pointer',
  zIndex: '1250 !important',
});

const getCallIconStyles = () => ({
  width: "25px",
  height: "25px",
  borderRadius: "0",
});

const Contact = ({
  userId,
  contact,
  thumbnail = null,
  onContactClicked,
  closingModal,
  onLine = false,
  showAdd = false,
  addContact = null,
  highlight,
  allProfileImages,
  startConversation,
  favourites,
  openRecharge,
  balance
}) => {
  const [openCallPopUp, setOpenCallPopUp] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState({});
  const [hasPhoneNumber, setHasPhoneNumber] = useState(true);
  const callButtonEl = useRef();

  const [imgSrc, setImgSrc] = useState('');

  useEffect(() => {
    fetchProfileImage()
  }, [contact, userId]);

  const fetchProfileImage = async () => {
    let imgSrc
    if (contact?.contactType === 'local') {
       imgSrc = getUserProfileImageUrlThumbnail(userId + contact?.userId)
    }
    else {
       imgSrc = getUserProfileImageUrlThumbnail(contact?.userId)
    }
    if (imgSrc) {
     const isValidSrc = await isValidImageUrl(imgSrc)
     if (isValidSrc) {
      setImgSrc(imgSrc);
     }
     else {
      setImgSrc("");
     }
    }
   

  };

  const topElStyle = highlight ? { backgroundColor: "#F4F4F4", height: "6rem", borderBottom: '1px solid #E5E5FF' } : { height: "5rem", borderBottom: '1px solid #E5E5FF', sx: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' } };
  if (onContactClicked) topElStyle.cursor = "pointer";

  const checkIsContactFavourite = () => {
    return favourites?.some(favourite => favourite?.contact?.userId === contact.userId);
  };


  const handleCall = () => {
    if (!contact) return;
    let data = contact.phoneNumbers;

    if (!data) return;

    let numbers = [];
    Object.keys(data)?.map((key, i) => {
      if (data[key].length > 0) {
        numbers.push(data[key]);
      }
    });

    let contactType = contact?.showVOIP ? "voip" : "phone";
    let to = contact?.showVOIP ? contact.userId : phoneNumber?.number;

    if (numbers.length === 0 && contactType === "phone") return setHasPhoneNumber(false);

    if (numbers.length === 1) {
      setPhoneNumber({ hasOnlyOne: true, number: numbers[0], contactType, to });
    } else {
      setPhoneNumber({ hasOnlyOne: false, contactType, to });
    }

    setHasPhoneNumber(true);
  }

  const getBadgeIcon = (contact) => {
    if (contact?.contactType === "local") return null;
    let { rankLevel1 } = contact;
    let rankLevel1SmallCase = (rankLevel1 || "").toLowerCase();

    let iconPath;
    switch (rankLevel1SmallCase) {
      case "shore":
        iconPath = SHORE_BADGE_ICON;
        break;
      case "ship":
        iconPath = SHIP_BADGE_ICON;
        break;
      default:
        iconPath = '';
    }
    return (iconPath &&
      <img loading="lazy" src={iconPath} alt={`${contact?.category} badge`} style={{ width: "1.56rem" }} />
    );
  };

  useEffect(() => {
    handleCall();
  }, [contact])

  return (
    <>
      <li onClick={(e) => onContactClicked && onContactClicked(e, imgSrc)} key={contact.userId} className={`list-item Contacts-hover ${onLine ? "position-relative !flex !justify-between !items-center" : "!flex !justify-between !items-center"}`} style={topElStyle}>
        <Grid container flexDirection='row' alignItems='center' justifyContent='space-between' className="!px-3 w-full">
          <Grid sx={alignGridItems}>
            <StyledBadge
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              badgeContent={getBadgeIcon(contact)}
            >
              {imgSrc ?
                <img
                  src={imgSrc}
                  className="!w-[40px] !h-[40px] real rounded-full bg-primarymain text-purple-50 flex items-center justify-center text-size-14"
                  alt="img"
                />
                :
                <div className="!w-[40px] !h-[40px] real rounded-full bg-primarymain text-purple-50 flex items-center justify-center text-size-14">
                  {(contact.userName ? contact.userName.split(" ").map((n) => n[0]).join("") : "")}
                </div>
              }
            </StyledBadge>

            <Box sx={{ ml: "0.9rem" }} className="list-body">
              <span className="list-title font600" style={getContactNameStyles()}>
                <Typography sx={{ fontFamily: 'Figtree-Regular', fontSize: '14px', fontWeight: '600' }}>{contact.name || contact.userName || ""}</Typography>
                {contact.type && (
                  <span className="list-title font500" style={{ color: "#666666" }}>
                    {" "} (Vessel)
                  </span>
                )}
              </span>
              <div style={getContactDetailsStyles()}>
                {contact?.rankLevel2?.trim().length > 0 && (
                  <Chip label={contact.rankLevel2.trim() || "Personal"} sx={{ backgroundColor: getColorForRankAndRoles(contact) }} />
                )}
                <span style={getContactRankStyles()}>{contact.rankLevel3}</span>
                {contact?.mentor && <MentorBadge>M</MentorBadge>}
                <Stack flexDirection={'column'} alignItems={'center'} justifyContent={'center'} sx={{ position: "relative" }}>
                  {checkIsContactFavourite() && (
                    <Box
                      component={'img'}
                      src={FAVORITE_STAR_SELECTED}
                      style={getFavouriteIconStyles(contact)}
                    />
                  )}
                </Stack>
              </div>
            </Box>
          </Grid>
          <Grid container flexDirection='row' alignItems='center' justifyContent='center' >

            <Grid>
              {contact?.showChats && contact?.contactType !== "local" && (
                <Stack flexDirection='column' alignItems='center'>
                  <a
                    className="!me-3"
                    disabled={!contact.showAcceptIgnoreMsg && !contact.waitingForConfirmation ? false : true}
                    onClick={() => startConversation(contact)}
                  >
                    <img
                      src={ImagePath.chatIcon}
                      style={getCallIconStyles()}
                    />
                  </a>
                </Stack>
              )}
            </Grid>

            <Grid  onClick={(e) => e.stopPropagation()} sx={alignGridItems}>
              {hasPhoneNumber && (
                <>
                  {(contact.contactType === "local" && !phoneNumber?.hasOnlyOne) ? (
                    <CachedImage
                      id="call"
                      imgKey="call"
                      image={ImagePath.callIcon}
                      style={getCallIconStyles()}
                      onClick={() => setOpenCallPopUp(true)}
                    />
                  ) : (
                    !phoneNumber.hasNone && (
                      <CallButton
                        balance={balance}
                        callType={contact?.showVOIP ? "voip" : "phone"}
                        from={contact.emailAddress}
                        to={contact?.showVOIP ? contact.userId : phoneNumber?.number}
                        closeCallHistory={() => console.log("close call history")}
                        localCall={contact.contactType === "local" ? "local" : null}
                        toUserId={contact.userId}
                        toName={contact.userName}
                        openGetRecharge={openRecharge}
                      >
                        <img
                          src={ImagePath.callIcon}
                          style={getCallIconStyles()}
                          onClick={() => console.info("VOIP call initiated")}
                        />
                      </CallButton>)
                  )}
                </>
              )}
            </Grid>
          </Grid>

        </Grid>
      </li>
    </>

  );
};


export default Contact;
